/**
 * UserActions - Módulo para visualizar el historial de acciones de un usuario
 */
import $ from 'jquery';
import 'datatables.net-bs5';
import 'datatables.net-responsive-bs5';
import { getSpanishLanguage, escapeHtml, escapeHtmlAttribute } from '@/helpers';
export class UserActions {
    constructor() {
        this.TABLE_ID = '#userActionsTable';
        this.dataTable = null;
        this.baseUrl = window.location.origin;
        // Obtener el ID del usuario del atributo data del elemento table
        const tableElement = document.querySelector(this.TABLE_ID);
        this.userId = parseInt(tableElement?.getAttribute('data-user-id') || '0');
        if (this.userId > 0) {
            this.init();
        }
        else {
            console.error('User ID not found');
        }
    }
    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.initializeModule();
            });
        }
        else {
            this.initializeModule();
        }
    }
    initializeModule() {
        this.initializeTable();
        this.setupEventListeners();
    }
    setupEventListeners() {
        // Escuchar cambios de tema para ajustar la tabla si es necesario
        window.addEventListener('theme-changed', () => {
            this.adjustTableForTheme();
        });
        // Manejar el botón de limpiar filtros
        const clearFiltersBtn = document.getElementById('clearFiltersBtn');
        if (clearFiltersBtn) {
            clearFiltersBtn.addEventListener('click', () => {
                this.clearFilters();
            });
        }
        // Manejar cambios en los filtros de fecha
        const startDateInput = document.getElementById('start_date');
        const endDateInput = document.getElementById('end_date');
        if (startDateInput && endDateInput) {
            const applyFilterBtn = document.getElementById('applyFilterBtn');
            if (applyFilterBtn) {
                applyFilterBtn.addEventListener('click', () => {
                    this.applyDateFilters();
                });
            }
        }
        // Delegación de eventos para botones de ver detalles
        document.addEventListener('click', (e) => {
            const target = e.target;
            const toggleDetailsBtn = target.closest('[data-action="toggle-details"]');
            if (toggleDetailsBtn) {
                e.preventDefault();
                this.toggleDetails(toggleDetailsBtn);
            }
        });
    }
    adjustTableForTheme() {
        if (this.dataTable) {
            this.dataTable.draw(false);
        }
    }
    initializeTable() {
        const tableElement = document.querySelector(this.TABLE_ID);
        if (!tableElement) {
            console.warn('User actions table element not found');
            return;
        }
        // Obtener filtros de la URL
        const urlParams = new URLSearchParams(window.location.search);
        const startDate = urlParams.get('start_date') || '';
        const endDate = urlParams.get('end_date') || '';
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        try {
            this.dataTable = $(this.TABLE_ID).DataTable({
                processing: true,
                serverSide: true,
                responsive: true,
                language: getSpanishLanguage(),
                ajax: {
                    url: `${this.baseUrl}/users/get-actions-list/${this.userId}`,
                    type: 'POST',
                    data: (d) => {
                        // Obtener los valores actuales de los inputs en cada petición
                        const startDateInput = document.getElementById('start_date');
                        const endDateInput = document.getElementById('end_date');
                        return {
                            ...d,
                            'csrf_token': csrfToken || '',
                            'start_date': startDateInput?.value || '',
                            'end_date': endDateInput?.value || ''
                        };
                    },
                    dataSrc: (json) => {
                        return json.data;
                    },
                    error: (xhr, error, thrown) => {
                        console.error('Error al cargar las acciones:', error);
                        const tbody = document.querySelector(`${this.TABLE_ID} tbody`);
                        if (tbody) {
                            tbody.innerHTML = '<tr><td colspan="5" class="text-center text-danger">Error al cargar los datos. Por favor, recargue la página.</td></tr>';
                        }
                    }
                },
                columns: [
                    {
                        data: 'date_user_action',
                        name: 'date_user_action',
                        className: 'text-nowrap',
                        render: (data) => this.renderDate(data)
                    },
                    {
                        data: 'name_action',
                        name: 'name_action',
                        className: 'text-nowrap',
                        render: (data) => this.renderActionName(data)
                    },
                    {
                        data: 'description_action',
                        name: 'description_action',
                        className: 'text-wrap'
                    },
                    {
                        data: 'ip_user_action',
                        name: 'ip_user_action',
                        className: 'text-nowrap',
                        render: (data) => this.renderIp(data)
                    },
                    {
                        data: 'valor_user_action',
                        name: 'valor_user_action',
                        className: 'text-nowrap',
                        render: (data, type, row) => this.renderDetails(data, row)
                    }
                ],
                order: [[0, 'desc']],
                pageLength: 25,
                lengthMenu: [[10, 25, 50, 100], [10, 25, 50, 100]],
                drawCallback: () => {
                    // Restaurar el estado de los detalles expandidos después de cada render
                    this.restoreExpandedDetails();
                }
            });
        }
        catch (error) {
            console.error('Error initializing DataTables:', error);
        }
    }
    renderDate(data) {
        try {
            const date = new Date(data);
            return `
                <i class="bi bi-calendar3 me-1"></i>
                ${date.toLocaleDateString('es-ES')} ${date.toLocaleTimeString('es-ES')}
            `;
        }
        catch (e) {
            return data;
        }
    }
    renderActionName(data) {
        return `<span class="badge bg-primary">${escapeHtml(data)}</span>`;
    }
    renderIp(data) {
        return `<code class="small">${escapeHtml(data)}</code>`;
    }
    renderDetails(data, row) {
        if (!data) {
            return '<span class="text-muted">-</span>';
        }
        try {
            const valor = JSON.parse(data);
            if (typeof valor === 'object' && valor !== null && Object.keys(valor).length > 0) {
                return `
                    <button type="button"
                            class="btn btn-sm btn-outline-info"
                            data-action="toggle-details"
                            data-row-id="${row.id_user_action}"
                            data-details="${escapeHtmlAttribute(data)}">
                        <i class="bi bi-eye me-1"></i>
                        Ver detalles
                    </button>
                    <div class="collapse mt-2 details-row" id="details-${row.id_user_action}">
                        <div class="card card-body bg-light">
                            <pre class="mb-0 small">${JSON.stringify(valor, null, 2)}</pre>
                        </div>
                    </div>
                `;
            }
        }
        catch (e) {
            // Si no es JSON válido, mostrar tal cual
        }
        return '<span class="text-muted">-</span>';
    }
    toggleDetails(button) {
        const rowId = button.getAttribute('data-row-id');
        if (!rowId)
            return;
        const detailsRow = document.getElementById(`details-${rowId}`);
        if (detailsRow) {
            const isHidden = detailsRow.classList.contains('show') === false;
            if (isHidden) {
                detailsRow.classList.add('show');
            }
            else {
                detailsRow.classList.remove('show');
            }
        }
    }
    restoreExpandedDetails() {
        // Lógica para restaurar detalles expandidos si se necesita
        // Por ahora no implementamos persistencia de estado
    }
    applyDateFilters() {
        // Recargar la tabla con los filtros actuales
        if (this.dataTable) {
            this.dataTable.ajax.reload();
        }
    }
    clearFilters() {
        const startDateInput = document.getElementById('start_date');
        const endDateInput = document.getElementById('end_date');
        if (startDateInput)
            startDateInput.value = '';
        if (endDateInput)
            endDateInput.value = '';
        // Recargar la tabla sin filtros
        if (this.dataTable) {
            this.dataTable.ajax.reload();
        }
    }
    /**
     * Obtiene la instancia del DataTable
     */
    getTable() {
        return this.dataTable;
    }
    /**
     * Recarga los datos de la tabla
     * @param resetPaging Si es true, resetea la paginación a la primera página
     */
    reload(resetPaging = false) {
        if (this.dataTable) {
            this.dataTable.ajax.reload(null, resetPaging);
        }
    }
}
export default UserActions;
